import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseDelayedVisibilityOptions {
  /** Verzoegerung vor dem Einblenden (ms) */
  delay?: number;
  /** Verzoegerung vor dem Ausblenden (ms) */
  hideDelay?: number;
  /** Wenn gesetzt, blendet `show` nichts ein */
  disabled?: boolean;
}

export interface DelayedVisibility {
  /** Aktueller Sichtbarkeitszustand */
  isVisible: boolean;
  /** Einblenden nach `delay` ms anfordern */
  show: () => void;
  /** Ausblenden nach `hideDelay` ms anfordern */
  hide: () => void;
}

/**
 * Verzoegertes Ein- und Ausblenden, wie es Tooltips und Popover brauchen.
 *
 * `show` und `hide` verwerfen jeweils den noch offenen Gegen-Timer, damit ein
 * schnelles Rein-Raus-Bewegen der Maus nicht zu Flackern fuehrt. Beim Unmount
 * werden beide Timer aufgeraeumt.
 *
 * Die Timer liegen bewusst in dieser Einheit und nicht in der Komponente: dort
 * wuerden die Timer-Refs von Handlern beruehrt, die an `cloneElement`
 * weitergereicht werden — ein Muster, das der React-Compiler nicht als sicher
 * nachweisen kann.
 */
export function useDelayedVisibility({
  delay = 0,
  hideDelay = 0,
  disabled = false,
}: UseDelayedVisibilityOptions = {}): DelayedVisibility {
  const [isVisible, setIsVisible] = useState(false);
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const show = useCallback(() => {
    if (disabled) return;

    clearTimeout(hideTimeoutRef.current);
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  }, [disabled, delay]);

  const hide = useCallback(() => {
    clearTimeout(showTimeoutRef.current);
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    return () => {
      clearTimeout(showTimeoutRef.current);
      clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  return { isVisible, show, hide };
}

export default useDelayedVisibility;
